import React, { useEffect, useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import { SortBy } from 'tea-component/lib/table/addons';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { SearchBox } from 'tea-component';
import { useHashtagTimeline } from '../hooks';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import moment from 'moment';
import { reviewsFormat } from '@op/utils/value';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import GPT, { getPublicationsPromptList } from '@op/components/GPT';
import SmallChart from '@op/pages/Overview/KeyMetrics/MentionsSentiment/components/SmallChart';
import { debounce } from 'lodash-es';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import { getLocale } from 'umi';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import StatusLoading from '@op/components/LoadingCommon';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, { channelShareRankingHashTagTitleTips } from '@op/components/BubbleCommon';
import { DBO_EC } from '@op/utils/format';
import { DEFAULT_MODAL } from '@op/components/GPT/constants';

interface IHashtags {
  gameInfo: any;
  filter: any;
  setSelectedHashtag: (value: string) => void;
}

const sortByOptions = [
  { value: 'views', text: 'View' },
  // { value: 'share', text: 'Market Share by View' },
  { value: 'followers', text: 'Potential Impressions' },
  { value: 'engagements', text: 'Engagement' },
  { value: 'publications', text: 'Publication' },
];

export default function Hashtags(props: IHashtags) {
  const { filter, gameInfo, setSelectedHashtag } = props;
  const { formatMessage } = useLanguage();
  const isEn = getLocale() === 'en-US';
  const { goPage } = useDrilldownParams();
  const [visible, setVisible] = useState(true);
  const currentGameInfo = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'views',
    order: 'desc',
  });

  const hashtagFilter = useMemo(() => {
    const { edition_unified_id, id_type, entity_type } = gameInfo || {};

    return {
      ...filter,
      keyword_kind: ['real_hashtag'],
      review_types: ['posts_text', 'posts_video'],
      edition_unified_id,
      id_type,
      entity_type,
      page_source: 'game_ranking',
      // sort_item: sortBy.by,
      // sort_model: sortBy.order,
    };
  }, [filter, gameInfo]);

  const [search, setSearch] = useState('');

  const handleOnSearch = (value: string | undefined) => {
    setSearch(value || '');
    setCurrentPage(1);
    buriedBtnPoint('SearchHashtag');
  };

  const handleOnClear = () => {
    setSearch('');
    setCurrentPage(1);
  };

  const filterParams = useMemo(() => {
    const { edition_unified_id, id_type, entity_type } = gameInfo || {};
    return {
      ...filter,
      edition_unified_id,
      id_type,
      entity_type,
      keyword_kind: ['real_hashtag'],
      review_types: ['posts_text', 'posts_video'],
      timeline_types: ['publications', 'views'],
      social_media_by_account: ['-99999'],
      social_media_by_keyword: ['-99999'],
      page: 1,
      page_size: 50,
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      ...(search ? { search_text: search } : {}),
    };
  }, [filter, gameInfo, search, sortBy]);

  const { data, loading, extraDataLoading, fetchMoreData, isMoreToFetch } =
    useHashtagTimeline({
      filter: filterParams,
    });

  const inputChange = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    [],
  );

  useEffect(() => {
    let timer = null;
    if (
      currentGameInfo.current &&
      currentGameInfo.current?.edition_unified_id !==
      gameInfo?.edition_unified_id
    ) {
      setVisible(false);
      timer = setTimeout(() => {
        setVisible(true);
      }, 100);
    }
    currentGameInfo.current = gameInfo;

    return () => {
      if (timer !== null) clearTimeout(timer);
    };
  }, [gameInfo]);

  const prompt = useMemo(() => {
    if (!gameInfo) {
      return '';
    }
    const promptList = getPublicationsPromptList({ gameInfo });
    return isEn ? promptList?.[0]?.['en-US'] : promptList?.[0]?.['zh-CN'];
  }, [gameInfo, isEn]);

  const { summaryRef, getSummary } = useGeySummary({ isPreviews: false });

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el || !search || !isMoreToFetch) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
        if (!extraDataLoading) {
          setCurrentPage((prev) => prev + 1);
          fetchMoreData(currentPage + 1);
        }
      }
    };

    const el = containerRef.current;
    el?.addEventListener('scroll', handleScroll);

    return () => el?.removeEventListener('scroll', handleScroll);
  }, [
    extraDataLoading,
    containerRef,
    search,
    fetchMoreData,
    setCurrentPage,
    isMoreToFetch,
    currentPage,
  ]);

  return (
    <CardCommon contentCard hasTitleArrow className={'dbo-hot-content-hashtag'}>
      <div className={'dbo-hot-content-hashtag-wrapper'} ref={containerRef}>
        <div
          className={'card-slot-title-hover'}
          onClick={() => {
            const { channel, ...restFilter } = filter;
            goPage({
              path: `/opinion/Community/Hashtag?gameid=${gameInfo?.edition_unified_id}`,
              filter: {
                ...restFilter,
                channel_social: channel,
                channel_comments: ['-99999'],
                channel_news: ['-99999'],
                social_media_by_account: ['-99999'],
                social_media_by_keyword: ['-99999'],
              },
              mode: EDrilldownMode.Local,
            });
            buriedBtnPoint('GotoTop50Hashtag');
          }}
        >
          <BubbleCommon tipsMap={channelShareRankingHashTagTitleTips} showIcon={false} placement='top'>
            <span>{formatMessage({ id: 'Top 50 Hashtags' })}</span>
          </BubbleCommon>

          <SvgIcon type="arrow-right" className="card-slot-icon" />
        </div>
        {visible && (
          <GPT.PageCard
            contentCard
            saveName="Channel Share Rankings"
            filter={hashtagFilter}
            ref={summaryRef}
            hasStopBtn
            showRefreshBtn={false}
          >
            {() => (
              <GPT.Button
                onClick={() => {
                  getSummary({
                    modelType: DEFAULT_MODAL,
                    resultType: 'page',
                    prompt,
                    gptOptions: 'basic',
                  });
                }}
              ></GPT.Button>
            )}
          </GPT.PageCard>
        )}

        <CardCommon
          contentCard
          title={
            <div className={'hashtag-filter'}>
              <SearchBox
                className={'hashtag-search-bar'}
                onSearch={handleOnSearch}
                onClear={handleOnClear}
                defaultValue={search}
                placeholder={formatMessage({ id: 'Search Hashtag' })}
                onChange={inputChange}
              />
              <SortBySelect
                defaultParams={{
                  options: sortByOptions,
                  btnTextPrefix: formatMessage({ id: 'Sort By' }),
                  appearance: 'default',
                }}
                defaultValue={sortBy.by}
                onChange={(_key: any, val: string) => {
                  setSortBy({ by: val, order: 'desc' });
                  // buriedBtnPoint('TPTP_Sort', { by: val, order: 'desc' });
                }}
              />
            </div>
          }
          loading={loading && !extraDataLoading}
          loadingHeight={600}
          empty={!data || data.length === 0}
          emptyHeight={600}
        >
          <>
            {data?.map((item: any, index: number) => {
              const {
                name,
                views,
                old_views,
                views_qoq,
                publications,
                old_publications,
                publications_qoq,
                oldTimeList,
                newTimeList,
                xAxis: x,
                views_timeline,
                publications_timeline,
                rank,
              } = item;

              let realRank = rank > 3 ? rank : '';
              if (rank === -1) {
                realRank = `> 50`;
              }

              return (
                <CardCommon
                  key={index}
                  title={
                    <div className={`hashtag-badge-wrapper`}>
                      <span className={`hashtag-badge rank-${rank}`}>
                        {realRank}
                      </span>
                      <span
                        title={`#${name}`}
                        className={'hashtag-name'}
                      >{`#${name}`}</span>
                    </div>
                  }
                  hasTitleArrow
                  handleTitleOnClick={() => {
                    setSelectedHashtag(name);
                    buriedBtnPoint('ClickHashtag', { hashtag: name });
                  }}
                  className={'dbo-hashtag-card'}
                >
                  <HashtagMetricsItem
                    labelId="Views"
                    name={name}
                    value={views}
                    oldValue={old_views}
                    qoq={views_qoq}
                    timeline={views_timeline}
                    xAxis={x}
                    oldTimeList={oldTimeList}
                    newTimeList={newTimeList}
                    filter={filter}
                  />
                  <HashtagMetricsItem
                    labelId="Publications"
                    name={name}
                    value={publications}
                    oldValue={old_publications}
                    qoq={publications_qoq}
                    timeline={publications_timeline}
                    xAxis={x}
                    oldTimeList={oldTimeList}
                    newTimeList={newTimeList}
                    filter={filter}
                  />
                </CardCommon>
              );
            })}
            {extraDataLoading && (
              <StatusLoading
                status="loading"
                style={{ height: 30 }}
              ></StatusLoading>
            )}
          </>
        </CardCommon>
      </div>
    </CardCommon>
  );
}

interface IHashtagMetricsItem {
  labelId: string;
  name: string;
  value: number;
  oldValue: number;
  qoq: number;
  timeline: any;
  xAxis: any;
  oldTimeList: any[];
  newTimeList: any[];
  filter: any;
}
const HashtagMetricsItem = (props: IHashtagMetricsItem) => {
  const {
    labelId,
    name,
    value,
    oldValue,
    qoq,
    timeline,
    xAxis,
    oldTimeList,
    newTimeList,
    filter,
  } = props;
  const { formatMessage } = useLanguage();
  return (
    <div className="hashtag-item-wrapper">
      <div className="hashtag-item">
        <span className="hashtag-item-label">
          {formatMessage({ id: labelId })}
        </span>
        <RingRatioBubble
          oldTime={oldTimeList}
          newTime={newTimeList}
          oldValue={oldValue}
          newValue={value}
          formateDate={(key: moment.MomentInput) =>
            !key ? '-' : moment(key).format('YYYY-MM-DD')
          }
          formateValue={(key: any) => reviewsFormat(key, 2)}
        >
          <div className="hashtag-item-value">
            {reviewsFormat(value)}
            {qoq !== 0 && <PercentageItem percentage={qoq} />}
          </div>
        </RingRatioBubble>
      </div>

      {timeline?.length > 0 && (
        <div className="hashtag-item-chart">
          <SmallChart
            text={`#${name}`}
            statistics={{ x: xAxis, y: timeline }}
            formatValueFunc={(val: number) => reviewsFormat(val)}
            dateType={filter?.date_type || 'daily'}
            topicName={formatMessage({ id: labelId })}
            seriesProps={{
              areaStyle: DBO_EC.gradientColor('#326FF0', 0, 0.4)
            }}
          />
        </div>
      )}
    </div>
  );
};
